import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/generalInsureStore";
import { deleteData, fetchData, postData } from "../../../services";
import { useSnackbar } from "../../../context/popup";
import UploadFile from "../../Utils/UploadFile";
import { useLoading } from "../../../components/Loading";
import GeneralInsurerFilterDialog from "./components/GeneralInsurerFilterDialog.js";

const GeneralInsurerList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector((state) => state?.generalInsurerStore?.currentPage);
  const rowsPerPage = useSelector((state) => state?.generalInsurerStore?.rowsPerPage);
  const totalPages = useSelector((state) => state?.generalInsurerStore?.totalPages);

  const [isRefresh, setIsRefresh] = useState(false);
  const [insurerData, setInsurerData] = useState([]);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
      isActive: "",
  });

  useEffect(() => {
    fetchInsurerData();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //table data fetch function
  const fetchInsurerData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/general/insurer-master?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;
            
      //for active and inactive status
      if(filterData.isActive !== "") {
          url += `&isActive=${filterData.isActive}`;
      }

      const response = await fetchData(url);

      setInsurerData(response?.data?.items);
      dispatch(setTotalPages(response?.data?.totalPages));
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    }
    finally {
      setCircleLoading(false);
    }
  };

  //bulk file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true)
    setUploadSuccess(false)

    try {
        const formData = new FormData()

        formData.append('file', file)

        const result = await postData(`/reference/master/bulkUpload/general-insurer`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });        

        if (result?.success) {
            setUploadSuccess(true)
            showSnackbar(result?.message,"success");
            setOpenUploadFileDialog(false);
            setClearFileOnSuccess(true);
            fetchInsurerData(); 
        }
        else {
            setUploadSuccess(false);
            showSnackbar('Faiiled to upload file', "error")
        }

    } catch (error) {
          const errorMessage = error?.response?.data?.message;                
          console.error('Upload error:', error || errorMessage)
          showSnackbar(errorMessage, "error")
    } 
    finally {
          setFileUploading(false)
    }
  };

  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "companyName",
      label: "Company Name",
      renderCell: (row) => row.companyName,
    },
    {
      key: "companyCode",
      label: "Company Code",
      renderCell: (row) => row.companyCode,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row.createdAt,
    },
    {
      key: "active",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      )
    },
  ];

  const rows = insurerData.map((insurer, index) => ({
    ...insurer,
    sno: index + 1,
    createdAt: new Date(insurer.createdAt).toLocaleDateString('en-GB'),
  }));

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(`/general-master/general-insurer/edit-insurer/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/general-master/general-insurer/view-insurer/${row?.id}`);
  };

  //delete api function
  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/reference/general/insurer-master-delete`, {ids:[row?.id]});
      if(response?.success){
        showSnackbar(response?.message, "success");
        setIsRefresh(!isRefresh);
      }
      else{
        showSnackbar("Failed to delete Motor Make", "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar("Failed to delete Motor Make", "error");
    }    
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewInsurer = () => {
    navigate("/general-master/general-insurer/add-insurer");
  };

  const handleFileUpload = () => {
    setOpenUploadFileDialog(true);
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                General Insurer management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                View and manage all registered insurer companies.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
              variant="gradient"
              color="dark"
              size="small"
              startIcon={<FilterAlt/>}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => handleFileUpload()}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewInsurer()}
              >
                Add Insurer
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) => handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel={"No General Insurer data found"}
          handleSearch={handleSearch}
        />
      </Card>

      <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={['csv', 'xlsx']}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
        clearFileOnSuccess={clearFileOnSuccess}
      />
      <GeneralInsurerFilterDialog
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      filterData={filterData}
      setFilterData={setFilterData}
      onApply={() => {
          setAnchorEl(null);   // close popup
          fetchInsurerData();  // call API after filter applied
      }}
      />
    </DashboardLayout>
  );
};

export default GeneralInsurerList;
